import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

const COLORES = ['#2563eb', '#16a34a', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9'];

const fmtDinero = v => '$ ' + Number(v || 0).toLocaleString('es-CO');
const fmtNum    = v => Number(v || 0).toLocaleString('es-CO');

const fmtFechaCorta = f => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  return isNaN(d) ? String(f).slice(0, 10) : d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
};

// "Hace 5 min" / "Hace 2 h" / "Hace 3 d"
const hace = f => {
  if (!f) return '';
  const d = new Date(String(f).replace(' ', 'T'));
  if (isNaN(d)) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1)    return 'Hace un momento';
  if (min < 60)   return `Hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)     return `Hace ${h} h`;
  const dias = Math.floor(h / 24);
  return `Hace ${dias} d`;
};

const ICONO_ACTIVIDAD = { negocio: '🏪', usuario: '👤', ticket: '🎫' };
const COLOR_ALERTA = { alta: '#ef4444', media: '#f59e0b', info: '#2563eb' };

export default function SuperAdminDashboard() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const esMovil = useIsMobile();

  useEffect(() => {
    api.get('/reports/superadmin')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  const kpis = [
    { label: 'Negocios totales',     valor: fmtNum(data?.total_businesses),     icono: '🏪', color: '#2563eb' },
    { label: 'Usuarios totales',     valor: fmtNum(data?.total_users),          icono: '👥', color: '#8b5cf6' },
    { label: 'Suscripciones activas',valor: fmtNum(data?.active_subscriptions), icono: '👑', color: '#16a34a' },
    { label: 'Ingreso mensual (MRR)',valor: fmtDinero(data?.mrr),               icono: '💰', color: '#f59e0b' },
  ];

  const crecimiento = (data?.growth || []).map(g => ({
    fecha: fmtFechaCorta(g.fecha),
    Negocios: Number(g.negocios) || 0,
    Usuarios: Number(g.usuarios) || 0,
  }));

  const ingresosPlan = (data?.revenue_by_plan || [])
    .map(p => ({ name: p.plan, value: Number(p.total) || 0, negocios: Number(p.negocios) || 0, precio: Number(p.precio) || 0 }))
    .filter(p => p.value > 0);

  const totalIngresosPlan = ingresosPlan.reduce((s, p) => s + p.value, 0);

  const ud = data?.users_distribution || {};
  const usuariosDona = [
    { name: 'Activos',   value: Number(ud.activos)   || 0, color: '#16a34a' },
    { name: 'Inactivos', value: Number(ud.inactivos) || 0, color: '#ef4444' },
  ].filter(u => u.value > 0);

  const s = data?.subscriptions || {};
  const estadoSusc = [
    { label: 'Activas',           valor: Number(s.activas)     || 0, color: '#16a34a' },
    { label: 'Por vencer (7 días)', valor: Number(s.por_vencer) || 0, color: '#f59e0b' },
    { label: 'Vencidas',          valor: Number(s.vencidas)    || 0, color: '#ef4444' },
    { label: 'Suspendidas',       valor: Number(s.suspendidas) || 0, color: '#64748b' },
  ];
  const totalSusc = estadoSusc.reduce((a, b) => a + b.valor, 0) || 1;

  const seccion = titulo => (
    <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 14 }}>{titulo}</div>
  );

  const tarjeta = { background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '1.1rem 1.25rem' };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-text">
          <h2>Dashboard</h2>
          <p>Resumen general de la plataforma</p>
        </div>
        <span className="badge badge-gold">Super Administrador</span>
      </div>

      {/* ── Tarjetas KPI ── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '1rem', marginBottom: '1.25rem',
      }}>
        {kpis.map(k => (
          <div key={k.label} style={tarjeta}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: k.color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              }}>{k.icono}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', lineHeight: 1.2 }}>
                {k.label}
              </div>
            </div>
            <div style={{ fontSize: esMovil ? '1.3rem' : '1.6rem', fontWeight: 800, color: 'var(--color-text)' }}>
              {k.valor}
            </div>
          </div>
        ))}
      </div>

      {/* ── Crecimiento + distribución de usuarios ── */}
      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '2fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ ...tarjeta, minWidth: 0 }}>
          {seccion('Crecimiento de negocios y usuarios (últimos 30 días)')}
          {crecimiento.length === 0 ? (
            <div style={{ padding: '2.5rem 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
              Sin registros nuevos en los últimos 30 días
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={crecimiento}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Negocios" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Usuarios" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={{ ...tarjeta, minWidth: 0 }}>
          {seccion('Usuarios')}
          {usuariosDona.length === 0 ? (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>Sin usuarios</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={usuariosDona} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={2}>
                    {usuariosDona.map((u, i) => <Cell key={i} fill={u.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {usuariosDona.map(u => (
                  <div key={u.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.83rem' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: u.color }} />
                      {u.name}
                    </span>
                    <strong>{fmtNum(u.value)}</strong>
                  </div>
                ))}
                {Number(ud.nuevos) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', color: 'var(--color-text-muted)', borderTop: '1px solid var(--color-border)', paddingTop: 6, marginTop: 2 }}>
                    <span>Nuevos este mes</span><strong>{fmtNum(ud.nuevos)}</strong>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Ingresos por plan + actividad reciente ── */}
      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1fr 1fr', gap: '1.25rem', marginBottom: '1.25rem' }}>
        <div style={{ ...tarjeta, minWidth: 0 }}>
          {seccion('Ingresos por plan (mensual)')}
          {ingresosPlan.length === 0 ? (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>
              Sin negocios activos con plan asignado
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={ingresosPlan} dataKey="value" nameKey="name" innerRadius={55} outerRadius={80} paddingAngle={2}>
                    {ingresosPlan.map((p, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => fmtDinero(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 4 }}>
                {ingresosPlan.map((p, i) => (
                  <div key={p.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.83rem', gap: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: COLORES[i % COLORES.length], flexShrink: 0 }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name} <span style={{ color: 'var(--color-text-muted)' }}>({p.negocios})</span>
                      </span>
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <strong>{fmtDinero(p.value)}</strong>{' '}
                      <span style={{ color: 'var(--color-text-muted)', fontSize: '0.78rem' }}>
                        {totalIngresosPlan ? `${Math.round(p.value * 100 / totalIngresosPlan)}%` : ''}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ ...tarjeta, minWidth: 0 }}>
          {seccion('Actividad reciente')}
          {(data?.recent_activity || []).length === 0 ? (
            <div style={{ padding: '2rem 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>Sin actividad reciente</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {data.recent_activity.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 11, padding: '9px 0', borderBottom: i < data.recent_activity.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--color-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {ICONO_ACTIVIDAD[a.tipo] || '•'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{a.titulo}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.detalle}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-faint, var(--color-text-muted))', whiteSpace: 'nowrap' }}>{hace(a.fecha)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Negocios nuevos + suscripciones + alertas ── */}
      <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1.4fr 1fr', gap: '1.25rem' }}>
        <div style={{ ...tarjeta, minWidth: 0 }}>
          {seccion('Negocios recientes')}
          {(data?.recent_businesses || []).length === 0 ? (
            <div style={{ padding: '1.5rem 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.88rem' }}>Sin negocios</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr><th>Negocio</th><th>Plan</th><th>Usuarios</th><th>Estado</th><th>Registro</th></tr>
                </thead>
                <tbody>
                  {data.recent_businesses.slice(0, 6).map(b => (
                    <tr key={b.id}>
                      <td style={{ fontWeight: 600 }}>{b.nombre}</td>
                      <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{b.plan_name || '—'}</span></td>
                      <td>{b.user_count}</td>
                      <td>
                        <span style={{ fontSize: '0.78rem', color: b.estado === 'active' ? '#16a34a' : '#ef4444' }}>
                          {b.estado === 'active' ? 'Activo' : b.estado === 'suspended' ? 'Suspendido' : b.estado}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{fmtFechaCorta(b.creado_en)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', minWidth: 0 }}>
          <div style={tarjeta}>
            {seccion('Estado de suscripciones')}
            {estadoSusc.map(e => (
              <div key={e.label} style={{ marginBottom: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 4 }}>
                  <span>{e.label}</span>
                  <strong>{fmtNum(e.valor)}</strong>
                </div>
                <div style={{ height: 6, borderRadius: 99, background: 'var(--color-surface)' }}>
                  <div style={{ width: `${(e.valor * 100 / totalSusc).toFixed(0)}%`, height: '100%', borderRadius: 99, background: e.color }} />
                </div>
              </div>
            ))}
          </div>

          <div style={tarjeta}>
            {seccion('Alertas del sistema')}
            {(data?.alerts || []).length === 0 ? (
              <div style={{ padding: '1rem 0', textAlign: 'center', color: '#16a34a', fontSize: '0.85rem' }}>
                ✅ Todo en orden, sin alertas
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.alerts.map((a, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 9, padding: '9px 11px', borderRadius: 9,
                    background: (COLOR_ALERTA[a.nivel] || '#64748b') + '14',
                    borderLeft: `3px solid ${COLOR_ALERTA[a.nivel] || '#64748b'}`,
                  }}>
                    <span style={{ flexShrink: 0 }}>⚠️</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.83rem', fontWeight: 600 }}>{a.titulo}</div>
                      <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{a.detalle}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
